import {
  WSFilterApiOptionsLoader,
  WSFilterOption
} from "@wingspanhq/fe-component-library";
import { SearchPayerRowsFilterParameter } from "@wingspanhq/wingspan-api-sdk";
import { useMemo, useState } from "react";
import { queriesPayerRows } from "@query";
import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";

/**
 * API-based payer-assignment filter options, with pagination and search.
 *
 * v3 read: `searchPayerRows` (was v1 `useQueryPayerRows`, whose search service
 * throws `NotMigrated` — so these options were always empty). The row carries
 * its assignments inline as `PayerRowEngagement[]`, which replaces v1's
 * `selectorPayerPayeeEngagements` + `selectorPayerPayeeEngagementName` pair:
 * the filtering they did is the `isDefault` check below — their
 * `labels.duplicate` half has no v3 row equivalent, see the TODO there — and
 * the name they derived is now the row's own `name`.
 *
 * @example
 * const engagementOptions = usePayerEngagementFilterOptions();
 *
 * const filters: WSFilter[] = [
 *   {
 *     title: "Engagement",
 *     name: "engagementId",
 *     type: "select",
 *     apiOptions: engagementOptions
 *   }
 * ];
 */
export function usePayerEngagementFilterOptions(
  baseFilter?: SearchPayerRowsFilterParameter
): WSFilterApiOptionsLoader {
  const [searchText, setSearchText] = useState("");

  const query = queriesPayerRows.searchPayerRows.usePaginatedQuery({
    filter: {
      ...baseFilter,
      ...(searchText ? { searchString: { eq: searchText } } : {})
    }
  });

  const rows = query.flatData;

  const options = useMemo(() => {
    const result: WSFilterOption<"checkbox">[] = [];

    (rows ?? []).forEach(payer => {
      const payerName = selectorSearchRowName(payer);

      // TODO(v3-blocker, WIN-16078): duplicate engagements are no longer excluded — see
      // `SelectPayerEngagement`. The v1 `labels.duplicate` flag lives on the
      // payer↔payee engagement, which the v3 search row's engagement item does
      // not carry.
      (payer.engagements ?? [])
        .filter(engagement => !engagement.isDefault && !!engagement.id)
        .forEach(engagement => {
          result.push({
            name: engagement.id as string,
            label: [payerName, engagement.name].filter(Boolean).join(" • "),
            helperText: undefined
          });
        });
    });

    return result;
  }, [rows]);

  return {
    data: options,
    isLoading: query.isInitialLoading,
    pagination: {
      hasNextPage: query.hasNextPage ?? false,
      isFetching: query.isFetching,
      isFetchingNextPage: query.isFetchingNextPage,
      fetchNextPage: query.fetchNextPage
    },
    search: {
      searchText,
      onSearchChange: setSearchText
    }
  };
}
